/**
 * @fileoverview トラブルシューティングページのトップコンテンツを記述したファイルです。
 */

import Accordion, { AccordionItemProps } from "../components/Accordion";
import LcdComModeJ from "./img/lcdComMode.png";
import LcdComModeE from "./img/English/lcdComModeE.png";
import { useTranslation } from "react-i18next";
/**
 * リンク先とリンクテキスト
 * @param to リンク先
 * @param text リンクテキスト
 * @returns
 */
interface BlankLinkProps {
  to: string;
  text: string;
}

/**
 *
 * @param
 * @returns 別ページでリンクを開くコンポーネント
 */
function BlankLink({ to, text }: BlankLinkProps) {
  return (
    <a
      href={to}
      className="c-angle c-blank"
      target="_blank"
      rel="noopener noreferrer"
    >
      {text}
    </a>
  );
}

/**
 * Help and support page
 * @returns {JSX.Element} トラブルシューティングページの JSX element
 */
export function HelpAndSupport(): JSX.Element {
  const { t, i18n } = useTranslation();

  //言語によって画像を切り替える
  const LcdComMode = i18n.language === "ja" ? LcdComModeJ : LcdComModeE;

  /**
   * 質問と回答のリスト GHS-8AT通信
   */
  const Ghs8atCommunicationsItems = [
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "USBケーブルを抜いた際にGHS-8ATが通信モードのままで測定モードにならない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "USBケーブルを素早く抜くとGHS-8AT側がUSBケーブルが抜かれたことを検出することができないために発生する場合があります。"
          )}
          {t("再度ケーブルを接続してゆっくりとUSBケーブルを抜いてください。")}
          <br />
          <img src={LcdComMode} alt={t("GHS-8AT通信モードと測定モード")} />
        </div>
      ),
    },
  ];

  /**
   * 質問と回答のリスト GHS-8ATインストール
   */
  const Ghs8atInstallItems = [
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "インストール途中で「L: ドライブがありません」といったエラーが表示されインストールが中断してしまう。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "通常LAN環境などに接続しているパソコンをスタンドアローン(LANに接続していない状態)で使用してインストールを行っために発生する場合があります。"
          )}
          {t("LAN環境に接続してから再度インストールを行ってください。")}
          {t("インストール後はLAN環境から外しても使用できます。")}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "GHS-8ATをUSBポートに接続するたびにドライバのインストール画面になる。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "USBポートを前回ドライバをインストールしたときに差込んでいたポートと別のポートに差込んだ場合にはUSBドライバを再度インストールする必要があります。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "GHS-8ATを接続しても通信状態にならない, または初めて接続してもドライバのインストールが始まらない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t("正しくインストールが行われていない可能性があります。")}
          {t(
            "GHS-8ATをパソコンに接続した状態で[システムのプロパティ]から[デバイスマネージャー]を起動します。"
          )}
          {t(
            "黄色い[?]や[!]のマークが表示されていた場合、以下の手順に従って再度デバイスドライバーのインストールを行ってください。"
          )}
          <BlankLink
            to="/anasys/HowToInstallDeviceDriver"
            text={t("デバイスドライバーのインストール方法")}
          />
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "ダウンロードでインストールを行ったが，アナシスが正常に動作しない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "ダウンロード時にダウンロードファイルの[保存]と[実行]を選択する画面で[実行]を選択しインストールを行うと正常にインストールができません。"
          )}
          {t(
            "一旦[保存]を選択しローカルのディスクにダウンロードファイルを保存後インストールを行ってください。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "ダウンロード後，インストールを実行すると「Error reading setup initialization file」といったエラーが表示されてインストールが実行できない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "ダウンロード時に対象ファイルの全容量をダウンロードすることができていません。"
          )}
          {t("再度ダウンロードを行いファイルを保存します。")}
          {t(
            "保存したファイルの容量が正しいか確認してからインストールを行ってください。"
          )}
        </div>
      ),
    },
  ];

  /**
   * 質問と回答のリスト アナシス操作
   */
  const AnasysOperationsItems = [
    {
      title: (
        <>
          {t("現象: ")}
          {t("画面表示の文字が大きくグラフに重なる。")}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t("画面のDPI値を変更している場合に発生します。")}
          {t("ディスプレイの画面設定を通常のサイズ(96dpi)に変更してください。")}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t("温度グラフ，温度軸，温度に関する表示が全て消えている。")}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "表示設定を温度表示を行わないに設定している場合には温度に関する全ての表示内容が表示されません。"
          )}
          {t(
            "メニュー-[オプション]-[表示内容の設定]を選択後温度表示を行わないのチェックボックスにチェックを入れてください。"
          )}
          {t("この設定は次回以降アナシスを使用する際にも反映されます。")}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "マウスカーソル値（画面左上に表示されるマウスの位置での濃度，温度，時間表示）が表示されない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "表示設定をマウスカーソル値の表示を行わないに設定している場合にはマウスカーソル値が表示されません。"
          )}
          {t(
            "メニュー-[オプション]-[表示内容の設定]を選択後，マウスカーソル値のチェックボックスにチェックを入れてください。"
          )}
          {t("この設定は次回以降アナシスを使用する際にも反映されます。")}
          {t(
            "また画面上でマウスカーソル値の表示位置を左クリックしてもマウスカーソル値の表示，非表示の設定が行えます。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t("入力したコメント，矢印が画面から消える。")}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t("コメント, 矢印の配置起点はX値は時間値,Y値は濃度値になります。")}
          {t(
            "濃度軸の拡大や時間軸の拡大操作を行いますとコメント,矢印の配置起点とした位置が表示範囲外となりその場合には画面上に表示されなくなります。"
          )}
          {t("濃度または時間軸を元の表示範囲に戻せば再び表示されます。")}
        </div>
      ),
    },
    i18n.language === "ja" && {
      title: (
        <>
          {t("現象: ")}
          {t(
            "ファイル保存時または読出し時に[環境依存している.lggには無効なパスが含まれています。]というエラーが発生し，保存，読出し操作ができない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t("ファイル名に環境依存文字を使用しています。")}
          {t("ファイル名に環境依存しない漢字を使用してください。")}
          <br />
          <BlankLink
            to="/anasys/HelpAndSupport/AnasysJis2004"
            text={t("環境依存文字の使用 注意点(詳細)")}
          />
        </div>
      ),
    },
    i18n.language === "ja" && {
      title: (
        <>
          {t("現象: ")}
          {t("識別名，グラフタイトル，コメントに ? が表示される。")}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "環境依存文字を使用しています。環境依存しない漢字を使用してください。"
          )}
          <br />
          <BlankLink
            to="/anasys/HelpAndSupport/AnasysJis2004"
            text={t("環境依存文字の使用 注意点(詳細)")}
          />
        </div>
      ),
    },
    {
      title: (
        <>
          {t("現象: ")}
          {t(
            "GHS-8ATを1台だけ接続しているが通信操作を行うと「GHS-8ATが複数台接続されています, GHS-8ATを1台だけ接続してください」とエラーが表示されて通信することができない。"
          )}
        </>
      ),
      content: (
        <div>
          {t("対処: ")}
          {t(
            "GHS-8AT以外のUSB機器（シリアル-USB変換器等）が接続されているとこの現象が発生する場合があります。"
          )}
          {t("他のUSB機器を外して再度通信を行ってください。")}
        </div>
      ),
    },
  ].filter(Boolean);

  /**
   * 質問と回答のリスト アナシス表示
   */
  const DisplayItems = [
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "画面左上のマウスカーソル値を消したいのですがなにか設定はありますか?"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "マウスカーソル値をクリックしてください。表示位置をクリックするたびに表示，非表示の切替ができます。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "画面右上に表示される最低濃度でマイナス表示をさせたくないのですが方法はありますか?"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "メニュー[オプション]-[表示内容の設定]を選択し濃度項目の[0ppm以下は0ppmとして表示]にチェックを入れてください。マイナス値を0ppmとして表示します。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "温度データは使用しないので表示させたくないのですが方法はありますか?"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "メニュー[オプション]-[表示内容の設定]を選択し[温度表示を行わない]のチェックを外してください。"
          )}
          {t("画面上の温度に関する表示内容を全て非表示とします。")}
        </div>
      ),
    },
  ];

  /**
   * 質問と回答のリスト アナシスファイル
   */
  const FileItems = [
    i18n.language === "ja" && {
      title: (
        <>
          {t("質問: ")}
          {t("旧アナシスバージョン4.1Jのファイルを読み込むことはできますか?")}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "アナシスバージョン6.03.000では旧アナシスバージョン4.1Jのファイル(*.gtl, *.gtg)を読み込めません。"
          )}
          <br />
          {t(
            "アナシスバージョン5.06.000でメニュー[ファイル]-[gtlファイルを開く]を選択し，過去に保存したロギングデータ(*.gtl)を読み込むことができます。"
          )}
          {t(
            "ただし旧バージョンのグラフファイル(*.gtg)は開くことができません。再度ロギングデータ(*.gtl)を開き新しいグラフファイル(*.lgg)として保存してください。"
          )}
          <br />
          <BlankLink
            to="/anasys/Version#Ver56Release"
            text={t("Ver5.06.000ダウンロード")}
          />
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "エクセルなどの表計算ソフトでデータを使いたいのですが方法はありますか?"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "メニュー[ファイル]-[CSVファイルへ変換]を選択しロギングデータをCSVファイルとして変換保存できます。"
          )}
          {t(
            "CSV変換保存したファイルは各種表計算ソフトで使用することができます。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "Windowsのエクスプローラー上からロギングファイル(*.lgd)やグラフファイル(*.lgx)を開くことはできますか?"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "エクスプローラー上でのファイルを開く操作（ダブルクリックによる操作含む）には対応しておりません。"
          )}
          {t(
            "保存したロギングファイル、グラフファイルを開くにはアナシスアプリケーションを起動しメニュー - [ファイル(F)]から各ファイルを開く操作を行ってください。"
          )}
        </div>
      ),
    },
  ].filter(Boolean);

  /**
   * 質問と回答のリスト アナシスアンインストール
   */
  const UninstallItems = [
    {
      title: (
        <>
          {t("質問: ")}
          {t("アナシスソフトウェアをアンインストールできますか?")}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "コントロールパネル-プログラムと機能からANASYS6を選択しアンインストールを行ってください。"
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t("デバイスドライバーをアンインストールできますか?")}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t("GHS-8ATをUSBポートに差し込み、デバイス マネジャーを起動します。")}
          {t(
            "[ユニバーサルシリアルバスコントローラー]内のGHS-8ATを右クリックし[デバイスのアンインストール]を選択します。"
          )}
          <br />
          {t("注: ")}
          {t(
            "複数台GHS-8ATをお持ちの場合過去にパソコンにつないだ全てのGHS-8ATを接続し同じ操作を繰り返す必要があります。"
          )}
        </div>
      ),
    },
  ];

  /**
   * 質問と回答 GHS-8AT測定値
   */
  const MeasurementItems = [
    {
      title: (
        <>
          {t("質問: ")}
          {t(
            "GHS-8ATでセンサ最小分解能以下の濃度の扱いについて教えてください。"
          )}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t(
            "最小分解能以下の濃度は全て下側の値に丸めて測定結果として扱われます。"
          )}
          {t("詳細は下記リンクページを参照してください。")}
          <br />
          <BlankLink
            to="/anasys/HelpAndSupport/Ghs8atMeasureConditions"
            text={t("GHS-8AT測定値の扱いと各ロギング条件の動作説明(詳細)")}
          />
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t("GHS-8ATのロギング条件について教えてください。")}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t("ロギング条件には瞬時, 最大, 最小, 平均の4つの条件があります。")}
          {t(
            "瞬時はロギング間隔に達したそのときの測定値，最大はロギング間隔中の最大値，最小はロギング間隔中の最小値，平均はロギング間隔中の平均になります。"
          )}
          {t(
            "例えば条件を平均とした場合にはロギング中は2秒に1回測定をおこなっていて1分間隔ロギングとすると30個のデータ中の平均値を算出します。"
          )}
          {t("詳細は下記リンクページを参照してください。")}
          <br />
          <BlankLink
            to="/anasys/HelpAndSupport/Ghs8atMeasureConditions"
            text={t("GHS-8AT測定値の扱いと各ロギング条件の動作説明(詳細)")}
          />
        </div>
      ),
    },
    {
      title: (
        <>
          {t("質問: ")}
          {t("GHS-8ATの最大ロギングデータ数はいくつですか?")}
        </>
      ),
      content: (
        <div>
          {t("回答: ")}
          {t("濃度, 温度の2つのデータをそれぞれ45960データ保存できます。")}
          {t("ロギング間隔1分で1ヶ月ロギングが可能です。")}
          {t(
            "ロギング条件を「温度をロギングしない」とした場合でもロギングできるデータ数は増加しません。(濃度データが45960個のまま)"
          )}
          {t(
            "ロギングデータが最大数まで達した場合にはデータの上書きは行われず自動的にロギングを停止します。"
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <section className="c-section">
        <h2 className="p-heading-2">{t("トラブルシューティング")}</h2>
        <p className="c-text">
          {t(
            "インストール時のエラーやパソコン環境により発生する現象それらの対処方法が記載されています。"
          )}
          {t(
            "また解決できない場合やお気づきの点ございましたらガステックまでお問い合わせください。"
          )}
        </p>

        <p className="c-link-list">
          <a
            href="http://www.gastec.co.jp/inquiry/"
            className="c-angle c-blank"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("ガステック お問い合わせページ")}
          </a>
        </p>

        <h2 className="p-heading-2" id="UsbComMode">
          {t("通信")}
        </h2>
        <Accordion items={Ghs8atCommunicationsItems} />

        <h2 className="p-heading-2">{t("インストールについて")}</h2>
        <Accordion items={Ghs8atInstallItems} />

        <h2 className="p-heading-2">{t("アナシス操作")}</h2>
        <Accordion items={AnasysOperationsItems as AccordionItemProps[]} />

        <h2 className="p-heading-2">Q and A</h2>
        <p className="c-text">
          {t(
            "GHS-8AT,アナシスソフトウェアに関して疑問点がありましたら以下のQandAから同様の質問を検索してください。"
          )}
        </p>

        <h2 className="p-heading-2">{t("表示")}</h2>
        <Accordion items={DisplayItems} />

        <h2 className="p-heading-2">{t("ファイル")}</h2>
        <Accordion items={FileItems as AccordionItemProps[]} />

        <h2 className="p-heading-2" id="Uninstall">
          {t("アンインストール")}
        </h2>
        <Accordion items={UninstallItems} />

        <h2 className="p-heading-2">
          {t("測定値，分解能，ロギング条件，ロギングデータ数")}
        </h2>
        <Accordion items={MeasurementItems} />
      </section>
    </>
  );
}
